// Combo (promotion) parsing helpers.
//
// Combos live in the promotions table with band_promotion === 2.
// `discount_percentage` is the FIXED combo price in MXN (NOT a percentage).
// `category_combination` is a JSON string:
//   { categories: number[], description: string, required_items: number,
//     main_items?: number[], allowed_items?: { [categoria]: number[] } }
// Categories: 1 Platillo, 2 Complemento, 3 Bebida, 4 Postre.
// `main_items` limita el combo a esos platillos, que salen de los combos
// genéricos. Misma regla que el backend (_combo_admite_platillo) y el POS.
// `allowed_items` limita los complementos/bebidas/postres de ESE combo sin
// apartarlos de los demás (backend _combo_admite_items).

const CAT_PLATILLO = 1;
const CAT_COMPLEMENTO = 2;

export const COMBO_BAND = 2;

export const CATEGORY_LABELS = {
  1: 'Platillo',
  2: 'Complemento',
  3: 'Bebida',
  4: 'Postre',
};

/**
 * Parse a single promotion row into a combo descriptor.
 * @returns {{ id, name, price, categories: number[], composition: string, requiredItems: number } | null}
 */
export function parseCombo(promo) {
  if (!promo || Number(promo.band_promotion) !== COMBO_BAND) return null;

  let combo = promo.category_combination;
  if (typeof combo === 'string') {
    try {
      combo = JSON.parse(combo);
    } catch {
      return null;
    }
  }

  let categories = [];
  if (Array.isArray(combo)) {
    categories = combo;
  } else if (combo && Array.isArray(combo.categories)) {
    categories = combo.categories;
  }
  categories = categories.map(Number).filter((n) => !Number.isNaN(n));
  if (categories.length === 0) return null;

  return {
    id: promo.id,
    name: promo.description || 'Combo',
    price: Number(promo.discount_percentage) || 0,
    categories,
    composition: compositionChips(categories).join(' · '),
    requiredItems: (combo && Number(combo.required_items)) || categories.length,
    employeeBenefit: Number(promo.employee_benefit) === 1,
    employeePrice: promo.employee_price != null ? Number(promo.employee_price) : null,
    mainItems: mainItemsOf(combo),
    allowedItems: allowedItemsOf(combo),
  };
}

function allowedItemsOf(combinacion) {
  const permitidos = combinacion && !Array.isArray(combinacion) ? combinacion.allowed_items : null;
  return permitidos && typeof permitidos === 'object' ? permitidos : {};
}

/** Si cada pieza [{cat, id}] cabe en las listas (allowed_items) del combo. */
function admiteItems(allowedItems, piezas) {
  return piezas.every(({ cat, id }) => {
    const lista = allowedItems[cat];
    return !Array.isArray(lista) || lista.length === 0 || lista.map(Number).includes(Number(id));
  });
}

/** Items que puede elegir el wizard en un paso de categoría distinta al platillo. */
export function opcionesDelCombo(combo, cat, items) {
  return items.filter((it) => admiteItems(combo?.allowedItems || {}, [{ cat, id: it.id }]));
}

function mainItemsOf(combinacion) {
  const lista = combinacion && !Array.isArray(combinacion) ? combinacion.main_items : null;
  return Array.isArray(lista) ? lista.map(Number) : [];
}

function combinacionDe(promo) {
  let combinacion = promo?.category_combination;
  if (typeof combinacion === 'string') {
    try {
      combinacion = JSON.parse(combinacion);
    } catch {
      return null;
    }
  }
  return combinacion;
}

/** Un combo con lista solo acepta sus platillos; uno genérico, todos menos los apartados. */
function admite(mainItems, mainId, reservados) {
  const id = mainId == null ? null : Number(mainId);
  return mainItems.length > 0 ? mainItems.includes(id) : !reservados.has(id);
}

const reservadosDe = (combos) => new Set(combos.flatMap((c) => c.mainItems || []));

/**
 * Combos (ya parseados) que puede armar ese platillo. `todos` es el catálogo
 * completo: si `combos` viene filtrado (p. ej. sin los que hoy no tienen
 * postre), la Cordon no debe "liberarse" y caer al Combo Juanita.
 */
export function combosParaPlatillo(combos, platilloId, todos = combos) {
  const reservados = reservadosDe(todos);
  return combos.filter((c) => admite(c.mainItems || [], platilloId, reservados));
}

/** Items que pueden ir en el paso del platillo de ese combo. */
export function platillosDelCombo(combo, combos, items) {
  const reservados = reservadosDe(combos);
  return items.filter((it) => admite(combo?.mainItems || [], it.id, reservados));
}

/**
 * Promoción de precio fijo con EXACTAMENTE esas categorías que admite el
 * platillo de la charola (sin platillo: solo combos genéricos) y esas piezas
 * [{cat, id}]. Si varias caben, la más barata. Recibe filas crudas de
 * /promotions/promotions.
 */
export function findPromoForCategories(categories, catalog, mainId = null, piezas = []) {
  return promosParaCategorias(categories, catalog, mainId, piezas)[0] || null;
}

/** Todas las de findPromoForCategories, de la más barata a la más cara. */
export function promosParaCategorias(categories, catalog, mainId = null, piezas = []) {
  const sorted = [...categories].map(Number).sort((a, b) => a - b);
  const reservados = new Set((catalog || []).flatMap((p) => mainItemsOf(combinacionDe(p))));
  const opciones = [];
  for (const promo of catalog || []) {
    if (Number(promo.band_promotion) !== COMBO_BAND) continue;
    const combinacion = combinacionDe(promo);
    if (!admite(mainItemsOf(combinacion), mainId, reservados)) continue;
    if (!admiteItems(allowedItemsOf(combinacion), piezas)) continue;
    const cats = Array.isArray(combinacion) ? combinacion : combinacion?.categories;
    if (!Array.isArray(cats)) continue;
    const promoSorted = cats.map(Number).sort((a, b) => a - b);
    if (promoSorted.length === sorted.length && promoSorted.every((v, i) => v === sorted[i])) {
      opciones.push(promo);
    }
  }
  return opciones.sort((a, b) => Number(a.discount_percentage) - Number(b.discount_percentage));
}

// Lo que cobra una charola recién armada ({main, sides} con price y
// applyPromotions): su combo si lo arma, si no la suma.
function precioCharola(ch, catalog) {
  const suma = ch.main.price + ch.sides.reduce((total, s) => total + s.price, 0);
  const promocionable = Number(ch.main.applyPromotions) === 1
    && ch.sides.every((s) => Number(s.applyPromotions) === 1);
  if (!promocionable || ch.sides.length < 2) return suma;
  const promo = findPromoForCategories(
    [CAT_PLATILLO, ...ch.sides.map(() => CAT_COMPLEMENTO)], catalog, ch.main.id,
    ch.sides.map((s) => ({ cat: CAT_COMPLEMENTO, id: s.id }))
  );
  return promo ? Number(promo.discount_percentage) : suma;
}

/**
 * Con combos que limitan complementos (allowed_items) el fill-first puede
 * repartir el puré en dos charolas y dejar ambas fuera del combo "sin puré".
 * Intercambia complementos (entre charolas y con los sobrantes, IN PLACE)
 * mientras baje el total. Sin listas no hace nada. Gemelo del panel
 * (packing.js, mejoraComplementos).
 */
export function mejoraComplementos(charolas, sobrantesArr, catalog) {
  const hayListas = (catalog || []).some((p) => {
    const lista = allowedItemsOf(combinacionDe(p))[CAT_COMPLEMENTO];
    return Array.isArray(lista) && lista.length > 0;
  });
  if (!hayListas) return;
  const sobrantes = { main: null, sides: sobrantesArr };
  const precio = (ch) => (ch === sobrantes
    ? ch.sides.reduce((total, s) => total + s.price, 0)
    : precioCharola(ch, catalog));
  const grupos = [...charolas, sobrantes];
  let mejoro = true;
  while (mejoro) {
    mejoro = false;
    for (let i = 0; i < grupos.length && !mejoro; i++) {
      for (let j = i + 1; j < grupos.length && !mejoro; j++) {
        const A = grupos[i];
        const B = grupos[j];
        for (let a = 0; a < A.sides.length && !mejoro; a++) {
          for (let b = 0; b < B.sides.length && !mejoro; b++) {
            if (A.sides[a].id === B.sides[b].id) continue;
            const antes = precio(A) + precio(B);
            [A.sides[a], B.sides[b]] = [B.sides[b], A.sides[a]];
            if (precio(A) + precio(B) < antes - 0.001) {
              mejoro = true;
            } else {
              [A.sides[a], B.sides[b]] = [B.sides[b], A.sides[a]];
            }
          }
        }
      }
    }
  }
}

/**
 * Lo que ahorra un platillo en su combo base (platillo + 2 guarniciones). Con
 * combos de distinto precio decide quién se queda con las guarniciones, igual
 * que el panel (management/src/Utils/packing.js, ahorroEnCombo).
 */
export function ahorroEnCombo(mainId, precio, catalog) {
  const base = findPromoForCategories([CAT_PLATILLO, CAT_COMPLEMENTO, CAT_COMPLEMENTO], catalog, mainId);
  return Number(precio || 0) - (base ? Number(base.discount_percentage) : 1e6);
}

/** Filter + parse a promotions list into combos only. */
export function parseCombos(promotions) {
  if (!Array.isArray(promotions)) return [];
  return promotions.map(parseCombo).filter(Boolean);
}

/**
 * Expand a category list into ordered wizard steps, numbering repeats.
 * [1,2,2,3] -> platillo, Complemento 1, Complemento 2, bebida
 */
export function buildSteps(categories) {
  const totals = {};
  categories.forEach((c) => {
    totals[c] = (totals[c] || 0) + 1;
  });

  const seen = {};
  return categories.map((cat, index) => {
    seen[cat] = (seen[cat] || 0) + 1;
    const label = CATEGORY_LABELS[cat] || `Categoría ${cat}`;
    const repeated = totals[cat] > 1;
    return {
      index,
      cat,
      label,
      title: repeated ? `${label} ${seen[cat]}` : `Elige tu ${label.toLowerCase()}`,
    };
  });
}

/** Human chips for composition, e.g. ["1 Platillo", "2 Complementos", "1 Bebida"]. */
export function compositionChips(categories) {
  const totals = {};
  categories.forEach((c) => {
    totals[c] = (totals[c] || 0) + 1;
  });
  return Object.entries(totals).map(([cat, n]) => {
    const label = CATEGORY_LABELS[cat] || `Cat ${cat}`;
    return `${n} ${n > 1 ? pluralize(label) : label}`;
  });
}

function pluralize(label) {
  return `${label}s`;
}

/**
 * Un suelto por categoría de `extraCats` que ese combo (fila cruda) acepte;
 * null si falta alguno. Mismo criterio que el panel y el backend (_elige_extras).
 */
export function eligeExtras(extraCats, libres, promo) {
  const permitidos = allowedItemsOf(combinacionDe(promo));
  const elegidos = [];
  for (const cat of extraCats) {
    const it = libres.find(
      (s) => s.categoryId === cat && !elegidos.includes(s) && admiteItems(permitidos, [{ cat, id: s.id }])
    );
    if (!it) return null;
    elegidos.push(it);
  }
  return elegidos;
}
